import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import GeneralTemplate from 'components/AppService/ReservationSettings/GeneralTemplate';
import SelectModeTemplate from 'components/AppService/ReservationSettings/SelectModeTemplate';
import LoadingWrapper from 'components/LoadingWrapper';
import {
  RESERVATION_SETTINGS_ALL_FIELDS,
  RESERVATION_SETTINGS_MAPPING,
  TEMPLATE_TYPE,
} from 'constants/ReservationMapping.constants';
import { usePrevious } from 'hooks/usePrevious';
import intl from 'i18n/intl';
import _, { find, get, isEmpty } from 'lodash';
import { fetchObjectMapping } from 'slices/objectRelationships.slice';
import {
  fetchObjectExtTypes,
  fetchReservationMapping,
  fetchReservationSituations,
  fetchSituationFields,
  fetchSituationTypes,
  fetchTypeFields,
  reservationSettingsLoading,
  reservationSettingsSelector,
  saveReservationMapping,
  situationTypesSelector,
  typeIdentifiedSelector,
  updateReservationSettingsLocal,
} from 'slices/reservationSettings.slice';
import { useAppDispatch } from 'slices/store';
import { EAppId, type IReservationParamMapping } from '@timeedit/types';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { Alert, Form } from '@timeedit/ui-components/antd';
import './reservationSettings.scss';

const language: any = intl.messages;

const byAppFieldId = (appFieldId: string) => (item: { appFieldId: string }) =>
  item.appFieldId === appFieldId;

const appName = EAppId.TE_EXAM;

const ReservationSettings = () => {
  const dispatch = useAppDispatch();
  const validationReservationSettings = {
    result: 0,
    message: '',
  };
  const reservationSettings = useSelector(reservationSettingsSelector);
  const situationTypes = useSelector(situationTypesSelector);
  const typeIdentified = useSelector(typeIdentifiedSelector);
  const loading = useSelector(reservationSettingsLoading);
  const reservationTestSettingsLoading = false;
  const [form] = Form.useForm();

  const { setFieldsValue } = form;

  const allFields = useMemo(
    () => RESERVATION_SETTINGS_ALL_FIELDS[appName] || {},
    [appName]
  );

  const prevReservationSettings = usePrevious(reservationSettings) || [];

  useEffect(() => {
    dispatch(fetchReservationMapping());
    dispatch(fetchObjectExtTypes());
    dispatch(fetchReservationSituations());
    dispatch(fetchObjectMapping(EAppId.TE_EXAM));
  }, []);

  const switchMapped: any = useMemo(
    () => ({
      fetchSituationFields: (value: string) =>
        dispatch(fetchSituationFields(value)),
      fetchSituationTypes: (value: string) =>
        dispatch(fetchSituationTypes(value)),
      fetchObjectTypeFields: (value: string) =>
        dispatch(fetchTypeFields(value)),
    }),
    []
  );

  useEffect(() => {
    reservationSettings.forEach(({ appFieldId, fieldExt }) => {
      setFieldsValue({ [appFieldId]: fieldExt });
      let onChange = get(allFields, [appFieldId, 'onChange']);
      const { fieldExt: preValue } = prevReservationSettings.find(
        byAppFieldId(appFieldId)
      ) || { fieldExt: undefined };
      const isChanged = fieldExt !== preValue;

      // onChange could be 1 string or array of strings (action names)
      if (onChange && isChanged && fieldExt) {
        if (!Array.isArray(onChange)) {
          onChange = [onChange];
        }

        for (const action of onChange) {
          if (switchMapped[action]) {
            switchMapped[action](fieldExt);
          }
        }
      }
    });
  }, [reservationSettings, allFields]);

  useEffect(() => {
    if (RESERVATION_SETTINGS_MAPPING[appName]) {
      RESERVATION_SETTINGS_MAPPING[appName].forEach(({ modeFieldId }) => {
        if (modeFieldId) {
          const modeFieldValue = (
            reservationSettings.find(
              ({ appFieldId }) => appFieldId === modeFieldId
            ) || {}
          ).fieldExt;
          if (modeFieldValue && !situationTypes[modeFieldValue]) {
            switchMapped.fetchSituationTypes(modeFieldValue);
            switchMapped.fetchSituationFields(modeFieldValue);
          }
        }
      });
    }
  }, [appName, reservationSettings]);

  const onChange = ({ appFieldId, fieldExt }: Record<string, string>) =>
    dispatch(updateReservationSettingsLocal({ appFieldId, fieldExt }));

  const onSave = useCallback(() => {
    dispatch(saveReservationMapping(reservationSettings));
  }, [appName, reservationSettings, typeIdentified]);

  const errorMessages = useMemo(() => {
    const errors: string[] = [];
    if (RESERVATION_SETTINGS_MAPPING[appName]) {
      RESERVATION_SETTINGS_MAPPING[appName].forEach(
        ({ fields, types, templateType, modeFieldId }) => {
          // check validation of mode field
          if (templateType === TEMPLATE_TYPE.SELECT_MODE) {
            const {
              required,
              requiredMessage,
              label,
              uniqWithFields,
              uniqWithFieldsMessage,
            } = fields[modeFieldId!];
            const field: Partial<IReservationParamMapping> =
              find(reservationSettings, {
                appFieldId: modeFieldId,
              }) || {};
            if (!field.fieldExt) {
              if (required) {
                errors.push(requiredMessage || `No ${label} selected`);
              } else {
                return;
              }
            } else {
              if (uniqWithFields) {
                const nonUnique = uniqWithFields.some((fieldId) => {
                  const fieldToCheck: Partial<IReservationParamMapping> =
                    find(reservationSettings, {
                      appFieldId: fieldId,
                    }) || {};
                  return !!(
                    fieldToCheck.fieldExt &&
                    fieldToCheck.fieldExt === field.fieldExt
                  );
                });
                if (nonUnique) {
                  errors.push(uniqWithFieldsMessage || 'Not unique');
                }
              }
            }
          }

          // check validation of all types
          Object.keys(types || {}).forEach((typeKey) => {
            const { required, requiredMessage, label } = types[typeKey];

            if (
              required &&
              (!_.get(typeIdentified, modeFieldId!) ||
                !_.get(typeIdentified, [modeFieldId!, typeKey]))
            ) {
              errors.push(requiredMessage || `${label} not detected`);
            }
          });

          // check validation of non-mode fields
          Object.keys(fields).forEach((appFieldId) => {
            const { required, requiredMessage, label } = _.get(
              fields,
              appFieldId
            );
            if (required && appFieldId !== modeFieldId) {
              const field = find(reservationSettings, { appFieldId });
              if (!field || !field.fieldExt) {
                errors.push(requiredMessage || `No ${label} selected`);
              }
            }
          });
        }
      );
    }
    return errors;
  }, [appName, reservationSettings, situationTypes]);

  const testResultMessages = useMemo(() => {
    const errors: string[] = [];
    if (validationReservationSettings.result !== 0) {
      errors.push(validationReservationSettings.message);
    }
    return errors;
  }, [validationReservationSettings]);

  const alert = useMemo(() => {
    // loading
    if (
      isEmpty(validationReservationSettings) ||
      reservationTestSettingsLoading
    ) {
      return (
        <Alert
          type="warning"
          title={<span>Testing...</span>}
          style={{ marginBottom: 20 }}
        />
      );
    }

    // success
    if (errorMessages.length === 0 && testResultMessages.length === 0) {
      return (
        <Alert
          type="success"
          title="Data mapping configuration looks good"
          style={{ marginBottom: 20 }}
        />
      );
    }

    // got errors
    return (
      <div style={{ marginBottom: 20 }}>
        {testResultMessages.length > 0 && (
          <Alert
            type="warning"
            title="Test result:"
            description={
              <>
                <ul>
                  {testResultMessages.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </>
            }
          />
        )}
        {errorMessages.length > 0 && (
          <Alert
            type="error"
            title="Errors:"
            description={
              <ul>
                {errorMessages.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
            }
          />
        )}
      </div>
    );
  }, [
    validationReservationSettings,
    reservationTestSettingsLoading,
    errorMessages,
    testResultMessages,
  ]);

  if (!RESERVATION_SETTINGS_MAPPING[appName]) {
    return (
      <Alert
        type="error"
        title="This application does not support Reservation settings"
      />
    );
  }

  return (
    <DefaultContentLayout
      title={language.application_exam_setting_title}
      subtitle={language.application_exam_reservation_setting_subtitle}
      actions={
        <Button type="primary" onClick={onSave}>
          {language.save_changes as string}
        </Button>
      }
    >
      <LoadingWrapper isLoading={loading}>
        <div className="reservation-settings">
          {alert}
          <Form
            layout="horizontal"
            labelAlign="left"
            labelCol={{ sm: { span: 24 }, md: { span: 12 }, xl: { span: 8 } }}
            wrapperCol={{ sm: { span: 24 }, md: { span: 12 }, xl: { span: 8 } }}
            form={form}
          >
            {(RESERVATION_SETTINGS_MAPPING[appName] as any[]).map(
              ({ templateType, showDependOn, ...configs }) => {
                // check dependent value
                if (showDependOn) {
                  const dependOnElement = reservationSettings.find(
                    ({ appFieldId }) => appFieldId === showDependOn
                  );
                  if (!dependOnElement || !dependOnElement.fieldExt)
                    return null;
                }

                switch (templateType) {
                  case TEMPLATE_TYPE.SELECT_MODE:
                    return (
                      <SelectModeTemplate {...configs} onChange={onChange} />
                    );
                  case TEMPLATE_TYPE.GENERAL:
                  default:
                    return (
                      <GeneralTemplate
                        {...configs}
                        onChange={onChange}
                        form={form}
                      />
                    );
                }
              }
            )}
          </Form>
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default ReservationSettings;
